// Singleton consumer to prevent multiple WebSocket connections
import { createConsumer } from "@rails/actioncable";
import { wrapConsumerForDebugging } from "./debugConsumer";
import { disableVisibilityDisconnect } from "./disableVisibilityDisconnect";
import { disableStaleDetection } from "./disableStaleDetection";

class ConsumerSingleton {
  constructor() {
    this.consumer = null;
    this.currentToken = null;
    this.currentOrgId = null;
  }

  getConsumer(token, orgId) {
    // Return existing consumer if token and org haven't changed
    if (this.consumer && this.currentToken === token && this.currentOrgId === orgId) {
      // Log to verify singleton is working
      if (!this.logged) {
        console.log('ConsumerSingleton: Returning existing consumer (singleton working!)');
        this.logged = true;
      }
      return this.consumer;
    }

    // Disconnect old consumer if it exists
    if (this.consumer) {
      console.log('ConsumerSingleton: Disconnecting old consumer due to token/org change');
      this.consumer.disconnect();
    }

    // Create new consumer if we have a token
    if (token) {
      const ws = document.querySelector('meta[name=action-cable-url]')?.content;
      if (ws) {
        const url = `${ws}?token=${token}&organization_id=${orgId || ''}`;
        console.log('ConsumerSingleton: Creating new consumer for', { token: token?.substring(0, 20) + '...', orgId });
        this.consumer = createConsumer(url);
        
        // Disable features that cause reconnection issues in production
        this.consumer = disableVisibilityDisconnect(this.consumer); // Don't disconnect on tab switch
        this.consumer = disableStaleDetection(this.consumer); // Don't reconnect on false "stale" detection
        
        // Wrap for debugging
        this.consumer = wrapConsumerForDebugging(this.consumer);
        
        // Add subscription debugging
        const originalCreate = this.consumer.subscriptions.create;
        this.consumer.subscriptions.create = function(channelInfo, callbacks) {
            console.log('🔵 Creating subscription:', channelInfo, {
                existingCount: this.subscriptions.length,
                connectionActive: this.consumer.connection.isActive()
            });
            const sub = originalCreate.call(this, channelInfo, callbacks);
            console.log('🔵 Subscription created, new count:', this.subscriptions.length);
            return sub;
        };
        
        this.currentToken = token;
        this.currentOrgId = orgId;
        this.logged = false;
        
        // Start monitoring connection health
        this.startMonitoring();
      }
    }

    return this.consumer;
  }

  disconnect() {
    if (this.consumer) {
      this.stopMonitoring();
      this.consumer.disconnect();
      this.consumer = null;
      this.currentToken = null;
      this.currentOrgId = null;
    }
  }
  
  // Force reconnection if state is out of sync
  ensureConnected() {
    if (!this.consumer) return false;
    
    const ws = this.consumer.connection.webSocket;
    const isWsOpen = ws?.readyState === 1;
    const isActive = this.consumer.connection.isActive();
    
    console.log('Connection state:', { wsOpen: isWsOpen, active: isActive });
    
    if (isWsOpen && !isActive) {
      // WebSocket is open but ActionCable thinks it's not active
      console.log('Fixing connection state mismatch');
      this.consumer.connection.monitor.recordConnect();
      return true;
    } else if (!isWsOpen && !isActive) {
      // Actually disconnected, need to reconnect
      console.log('Reconnecting...');
      this.consumer.connection.open();
      return false;
    }
    
    return isActive;
  }
  
  // Start monitoring connection health
  startMonitoring() {
    if (this.monitorInterval) return;

    this.monitorInterval = setInterval(() => {
      if (!this.consumer) return;

      const isActive = this.consumer.connection.isActive();
      const wsState = this.consumer.connection.webSocket?.readyState;

      // Check if connection is closed (readyState 3) or not active
      if (!isActive || wsState === 3) {
        console.log('🔄 Connection lost, attempting reconnection...', { isActive, wsState });

        // Dispatch event for React components to handle (e.g., trigger polling fallback)
        window.dispatchEvent(new CustomEvent('actionCableReconnecting'));

        this.consumer.connection.open();
      }
    }, 5000); // Check every 5 seconds
  }
  
  stopMonitoring() {
    if (this.monitorInterval) {
      clearInterval(this.monitorInterval);
      this.monitorInterval = null;
    }
  }
}

// Create singleton instance
const consumerSingleton = new ConsumerSingleton();

// Make it available globally for debugging
if (typeof window !== 'undefined') {
  window.actionCableConsumer = consumerSingleton;
  
  // Add debug toggle command
  window.actionCableDebug = {
    show: () => {
      window.showActionCableDebug = true;
      localStorage.setItem('showActionCableDebug', 'true');
      window.dispatchEvent(new Event('actionCableDebugToggle'));
      console.log('✅ ActionCable debug panel shown');
    },
    hide: () => {
      window.showActionCableDebug = false;
      localStorage.removeItem('showActionCableDebug');
      window.dispatchEvent(new Event('actionCableDebugToggle'));
      console.log('✅ ActionCable debug panel hidden');
    },
    toggle: () => {
      window.showActionCableDebug = !window.showActionCableDebug;
      if (window.showActionCableDebug) {
        localStorage.setItem('showActionCableDebug', 'true');
      } else {
        localStorage.removeItem('showActionCableDebug');
      }
      window.dispatchEvent(new Event('actionCableDebugToggle'));
      console.log(`✅ ActionCable debug panel ${window.showActionCableDebug ? 'shown' : 'hidden'}`);
    },
    reconnect: () => {
      if (consumerSingleton.consumer) {
        console.log('🔄 Forcing reconnection...');
        consumerSingleton.consumer.connection.open();
      } else {
        console.log('❌ No consumer to reconnect');
      }
    },
    status: () => {
      if (consumerSingleton.consumer) {
        const c = consumerSingleton.consumer;
        console.log('ActionCable Status:', {
          isActive: c.connection.isActive(),
          state: c.connection.getState(),
          webSocketState: c.connection.webSocket?.readyState,
          subscriptions: c.subscriptions.subscriptions.length,
          monitoring: !!consumerSingleton.monitorInterval
        });
      } else {
        console.log('❌ No consumer available');
      }
    }
  };
  
  // Restore debug state from localStorage
  if (localStorage.getItem('showActionCableDebug') === 'true') {
    window.showActionCableDebug = true;
  }
  
  console.log('💡 ActionCable Debug Commands:');
  console.log('  actionCableDebug.show()     - Show debug panel');
  console.log('  actionCableDebug.hide()     - Hide debug panel');
  console.log('  actionCableDebug.toggle()   - Toggle debug panel');
  console.log('  actionCableDebug.reconnect() - Force reconnection');
  console.log('  actionCableDebug.status()   - Check connection status');
}

export default consumerSingleton;